/**
 * Shopify Service
 * Shopify API entegrasyonu için servis sınıfı
 */

import api from '../lib/api';
import { formatDate } from '@/lib/format';

export interface ShopifyOrder {
  id: number;
  orderNumber: string;
  confirmationNumber: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  currency: string;
  totalPrice: string;
  currentTotalPrice: string;
  subtotalPrice: string;
  totalTax: string;
  totalDiscounts: string;
  financialStatus: string;
  fulfillmentStatus: string | null;
  cancelled: boolean;
  cancelledAt: string | null;
  cancelReason: string | null;
  processedAt: string;
  customer: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    verified: boolean;
  } | null;
  billingAddress: {
    first_name: string;
    last_name: string;
    company: string | null;
    address1: string;
    address2: string | null;
    city: string;
    zip: string;
    country: string;
    phone: string;
  };
  shippingAddress: {
    first_name: string;
    last_name: string;
    company: string | null;
    address1: string;
    address2: string | null;
    city: string;
    zip: string;
    country: string;
    phone: string;
  };
  lineItems: Array<{
    id: number;
    productId: number;
    variantId: number;
    title: string;
    name: string;
    sku: string;
    vendor: string;
    quantity: number;
    price: string;
    grams: number;
    fulfillmentStatus: string | null;
    fulfillmentService: string;
    giftCard: boolean;
    taxable: boolean;
    totalDiscount: string;
    variantTitle: string;
  }>;
  shippingLines: Array<{
    id: number;
    title: string;
    code: string;
    price: string;
    carrierIdentifier: string | null;
    requestedFulfillmentServiceId: string | null;
  }>;
  discountCodes: Array<{
    code: string;
    amount: string;
    type: string;
  }>;
  discountApplications: Array<{
    target_type: string;
    type: string;
    value: string;
    value_type: string;
    allocation_method: string;
    target_selection: string;
    code: string;
  }>;
  fulfillments: any[];
  refunds: any[];
  tags: string;
  note: string | null;
  noteAttributes: any[];
  orderStatusUrl: string;
  adminUrl?: string;
}

export interface ShopifyApiResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    orderCount: number;
    orders: ShopifyOrder[];
    meta: {
      shop: string;
      apiVersion: string;
      requestedAt: string;
      filters: {
        limit: number;
        status: string;
      };
    };
  };
}

export interface ShopifyOrderResponse {
  success: boolean;
  message: string;
  data: {
    order: ShopifyOrder;
    meta: {
      shop: string;
      apiVersion: string;
      requestedAt: string;
    };
  };
}

class ShopifyService {
  /**
   * Email adresine göre siparişleri getirir
   */
  async getOrdersByEmail(email: string, organizationId?: string, limit = 10, status = 'any', ticketId?: string): Promise<ShopifyApiResponse> {
    const response = await api.get<ShopifyApiResponse>(`/shopify/orders/${email}`, {
      params: {
        limit,
        status,
        organizationId: organizationId,
        // Ohne Treffer zur E-Mail sucht das Backend per Bestellnummer aus dem Ticket
        ticketId
      }
    });
    return response.data;
  }

  /**
   * Sipariş ID'sine göre sipariş detayını getirir
   */
  async getOrderById(orderId: string | number, organizationId?: string): Promise<ShopifyOrderResponse> {
    const response = await api.get<ShopifyOrderResponse>(`/shopify/orders/id/${orderId}`, {
      params: { organizationId: organizationId }
    });
    return response.data;
  }

  /**
   * Mağaza bilgilerini getirir
   */
  async getShopInfo(): Promise<any> {
    const response = await api.get(`/shopify/shop`);
    return response.data;
  }

  /**
   * Shopify order verilerini frontend için uygun formata çevirir
   */
  formatOrderForUI(order: ShopifyOrder) {
    return {
      id: (order as any).id,                    // Shopify order id — ödeme durumu/iade (PaymentOrderStatus) için
      orderNumber: order.orderNumber,
      confirmationNumber: order.confirmationNumber,
      date: formatDate(order.createdAt),
      paymentStatus: this.translatePaymentStatus(order.financialStatus),
      fulfillmentStatus: this.translateFulfillmentStatus(order.fulfillmentStatus),
      trackingNumber: (order as any).trackingNumber || null, // Sendungsverfolgung (trackba live)
      carrier: (order as any).carrier || null,
      total: `${order.totalPrice} ${order.currency}`,
      currency: order.currency,
      items: order.lineItems.map(item => ({
        sku: item.sku,
        name: item.name,
        quantity: item.quantity,
        price: `${item.price} ${order.currency}`,
        vendor: item.vendor,
        warehouse: (item as any).warehouse // Versandprofil = iç depo adı (backend'den gelir)
      })),
      customer: order.customer ? {
        id: order.customer.id.toString(),
        name: `${order.customer.firstName} ${order.customer.lastName}`,
        email: order.customer.email,
        phone: order.customer.phone,
        verified: order.customer.verified
      } : null,
      shippingAddress: {
        name: `${order.shippingAddress.first_name} ${order.shippingAddress.last_name}`,
        company: order.shippingAddress.company,
        address1: order.shippingAddress.address1,
        address2: order.shippingAddress.address2,
        city: order.shippingAddress.city,
        zip: order.shippingAddress.zip,
        country: order.shippingAddress.country,
        phone: order.shippingAddress.phone,
        fullAddress: `${order.shippingAddress.address1}${order.shippingAddress.address2 ? ', ' + order.shippingAddress.address2 : ''}, ${order.shippingAddress.zip} ${order.shippingAddress.city}, ${order.shippingAddress.country}`
      },
      billingAddress: {
        name: `${order.billingAddress.first_name} ${order.billingAddress.last_name}`,
        company: order.billingAddress.company,
        address1: order.billingAddress.address1,
        address2: order.billingAddress.address2,
        city: order.billingAddress.city,
        zip: order.billingAddress.zip,
        country: order.billingAddress.country,
        phone: order.billingAddress.phone,
        fullAddress: `${order.billingAddress.address1}${order.billingAddress.address2 ? ', ' + order.billingAddress.address2 : ''}, ${order.billingAddress.zip} ${order.billingAddress.city}, ${order.billingAddress.country}`
      },
      trackingNumbers: this.extractTrackingNumbers(order),
      discounts: order.discountCodes,
      shopType: 'Shopify',
      orderStatusUrl: order.orderStatusUrl,
      adminUrl: order.adminUrl
    };
  }

  /**
   * Payment status'u Almancaya çevirir
   */
  private translatePaymentStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'paid': 'Bezahlt',
      'pending': 'Ausstehend',
      'authorized': 'Autorisiert',
      'partially_paid': 'Teilweise bezahlt',
      'refunded': 'Rückerstattet',
      'voided': 'Storniert',
      'partially_refunded': 'Teilweise rückerstattet'
    };
    return statusMap[status] || status;
  }

  /**
   * Fulfillment status'u Almancaya çevirir
   */
  private translateFulfillmentStatus(status: string | null): string {
    if (!status) return 'Ausstehend';
    
    const statusMap: { [key: string]: string } = {
      'fulfilled': 'Versandt',
      'partial': 'Teilweise versandt',
      'restocked': 'Wieder eingelagert',
      'unfulfilled': 'Nicht versandt',
      'pending': 'Ausstehend'
    };
    return statusMap[status] || status;
  }

  /**
   * Fulfillment bilgilerinden tracking numaralarını çıkarır
   */
  private extractTrackingNumbers(order: ShopifyOrder): Array<{
    number: string;
    url?: string;
    carrier?: string;
  }> {
    const trackingNumbers: Array<{
      number: string;
      url?: string;
      carrier?: string;
    }> = [];

    if (order.fulfillments && order.fulfillments.length > 0) {
      order.fulfillments.forEach((fulfillment: any) => {
        if (fulfillment.tracking_numbers && fulfillment.tracking_numbers.length > 0) {
          fulfillment.tracking_numbers.forEach((trackingNumber: string, index: number) => {
            trackingNumbers.push({
              number: trackingNumber,
              url: fulfillment.tracking_urls?.[index] || undefined,
              carrier: fulfillment.tracking_company || undefined
            });
          });
        }
      });
    }

    return trackingNumbers;
  }
}

export const shopifyService = new ShopifyService();
export default shopifyService;